import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { Button, Form, Input, Modal, Space, Table } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { useState } from 'react'
import {
  useAddRegion,
  useDeleteRegion,
  useRegions,
  useUpdateRegion,
} from '../hooks/useRegions'
import { getPagination } from '../components/TablePagination'
import type { Region, RegionQueryParams } from '../services/regionService'
import type { ColumnsType } from 'antd/es/table'

// 定义搜索参数类型
interface RegionSearchParams {
  pageNum?: number
  pageSize?: number
  name?: string
  singlePrice?: string
  introduction?: string
}

export const Route = createFileRoute('/region')({
  component: RegionPage,
  validateSearch: (search: Record<string, unknown>): RegionSearchParams => {
    return {
      pageNum: Number(search.pageNum) || 1,
      pageSize: Number(search.pageSize) || 10,
      name: (search.name as string) || undefined,
      singlePrice: (search.singlePrice as string) || undefined,
      introduction: (search.introduction as string) || undefined,
    }
  },
})

function RegionPage() {
  // 获取 URL 搜索参数和导航函数
  const search = useSearch({ from: '/region' })
  const navigate = useNavigate({ from: '/region' })

  // 状态管理
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentRegion, setCurrentRegion] = useState<Region | null>(null)
  const [form] = Form.useForm()

  // 从 URL 参数构建查询参数
  const queryParams: RegionQueryParams = {
    pageNum: search.pageNum || 1,
    pageSize: search.pageSize || 10,
    name: search.name,
    singlePrice: search.singlePrice,
    introduction: search.introduction,
  }

  // 更新 URL 搜索参数的函数
  const updateSearchParams = (updates: Partial<RegionSearchParams>) => {
    navigate({
      search: (prev) => ({ ...prev, ...updates }),
    })
  }

  // API hooks
  const { data: regionData, isLoading } = useRegions(queryParams)
  const addRegionMutation = useAddRegion()
  const updateRegionMutation = useUpdateRegion()
  const deleteRegionMutation = useDeleteRegion()

  const columns: ColumnsType<Region> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '地区名称',
      dataIndex: 'name',
      key: 'name',
      filteredValue: search.name ? [search.name] : undefined,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="搜索地区名称"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => {
              confirm?.()
              updateSearchParams({
                name: selectedKeys[0] as string,
                pageNum: 1,
              })
            }}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              type="primary"
              onClick={() => {
                confirm?.()
                updateSearchParams({
                  name: selectedKeys[0] as string,
                  pageNum: 1,
                })
              }}
              size="small"
              style={{ width: 90 }}
            >
              搜索
            </Button>
            <Button
              onClick={() => {
                clearFilters?.()
                updateSearchParams({ name: undefined, pageNum: 1 })
              }}
              size="small"
              style={{ width: 90 }}
            >
              重置
            </Button>
          </div>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
    },
    {
      title: '单人间价格',
      dataIndex: 'singlePrice',
      key: 'singlePrice',
      render: (price: number) => `¥${price}`,
      filteredValue: search.singlePrice ? [search.singlePrice] : undefined,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="搜索价格"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => {
              confirm?.()
              updateSearchParams({
                singlePrice: selectedKeys[0] as string,
                pageNum: 1,
              })
            }}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              type="primary"
              onClick={() => {
                confirm?.()
                updateSearchParams({
                  singlePrice: selectedKeys[0] as string,
                  pageNum: 1,
                })
              }}
              size="small"
              style={{ width: 90 }}
            >
              搜索
            </Button>
            <Button
              onClick={() => {
                clearFilters?.()
                updateSearchParams({ singlePrice: undefined, pageNum: 1 })
              }}
              size="small"
              style={{ width: 90 }}
            >
              重置
            </Button>
          </div>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
    },
    {
      title: '房间描述',
      dataIndex: 'introduction',
      key: 'introduction',
      ellipsis: true,
      filteredValue: search.introduction ? [search.introduction] : undefined,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="搜索描述"
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => {
              confirm?.()
              updateSearchParams({
                introduction: selectedKeys[0] as string,
                pageNum: 1,
              })
            }}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              type="primary"
              onClick={() => {
                confirm?.()
                updateSearchParams({
                  introduction: selectedKeys[0] as string,
                  pageNum: 1,
                })
              }}
              size="small"
              style={{ width: 90 }}
            >
              搜索
            </Button>
            <Button
              onClick={() => {
                clearFilters?.()
                updateSearchParams({ introduction: undefined, pageNum: 1 })
              }}
              size="small"
              style={{ width: 90 }}
            >
              重置
            </Button>
          </div>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Button size="small" danger onClick={() => handleDelete(record.id)}>
            删除
          </Button>
        </Space>
      ),
    },
  ]

  const handleAdd = () => {
    setCurrentRegion(null)
    form.resetFields()
    setIsModalOpen(true)
  }

  const handleEdit = (region: Region) => {
    setCurrentRegion(region)
    form.setFieldsValue(region)
    setIsModalOpen(true)
  }

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除该地区吗？',
      onOk: () => {
        deleteRegionMutation.mutate(id)
      },
    })
  }

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      if (currentRegion) {
        // 编辑
        updateRegionMutation.mutate(
          { ...values, id: currentRegion.id },
          {
            onSuccess: () => {
              setIsModalOpen(false)
            },
          },
        )
      } else {
        // 新增
        addRegionMutation.mutate(values, {
          onSuccess: () => {
            setIsModalOpen(false)
          },
        })
      }
    })
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    updateSearchParams({
      pageNum: page,
      pageSize,
    })
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">住宿管理</h2>
        <Button type="primary" onClick={handleAdd}>
          添加住宿
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={regionData?.records || []}
        rowKey="id"
        loading={isLoading}
        bordered
        pagination={getPagination(
          regionData?.current || 1,
          regionData?.size || 10,
          regionData?.total || 0,
          handlePaginationChange,
        )}
      />

      <Modal
        title={currentRegion ? '编辑住宿' : '添加住宿'}
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={() => setIsModalOpen(false)}
        confirmLoading={
          addRegionMutation.isPending || updateRegionMutation.isPending
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="地区名称"
            rules={[{ required: true, message: '请输入地区名称' }]}
          >
            <Input placeholder="请输入地区名称" />
          </Form.Item>
          <Form.Item
            name="singlePrice"
            label="单人间价格"
            rules={[
              { required: true, message: '请输入价格' },
              {
                validator: (_, value) =>
                  Number(value) > 0
                    ? Promise.resolve()
                    : Promise.reject('价格必须大于 0'),
              },
            ]}
          >
            <Input placeholder="请输入单人间价格" />
          </Form.Item>
          <Form.Item
            name="introduction"
            label="房间描述"
            rules={[{ required: true, message: '请输入房间描述' }]}
          >
            <Input.TextArea placeholder="描述不同房间的区别和价格" rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
